import tw, { styled } from 'twin.macro';
import Icon from '@/components/Icon/IconSprite';
import { variants } from '@/components/Link/Link.styles';
import { StyledLink } from './components/ColumnSection/ColumnSectionLink.styles';

export const StyledButton = styled.button`
  ${tw`relative`}
  ${tw`z-50`}
  ${tw`w-full lg:w-auto`}
  ${tw`lg:cursor-default`}
`;

export const StyledIcon = styled(Icon)`
  ${tw`ml-auto lg:ml-0`}
  ${tw`transition-[transform linear 0.2s]`}
  ${tw`shrink-0`}
  ${tw`text-primary-p1`}
  ${tw`rotate-[-90deg] lg:rotate-[0deg]`}
  ${tw`lg:translate-x-[2px] lg:translate-y-[1px]`}
  ${tw`w-[21px] lg:w-[18px]`}
`;

export const StyledMenuItem = styled.span<{
  isActive: boolean;
  isHighlighted: boolean;
  isDesktop: boolean;
}>`
  ${tw`px-[21px] py-3 lg:p-0`}
  ${tw`lg:whitespace-nowrap`}
  ${tw`cursor-pointer`}

  ${StyledIcon} {
    ${({ isActive }) => isActive && tw`scale-y-[-1]`}
  }

  ${({ isDesktop }) => (isDesktop ? variants.nav_text_link : [tw`flex`])}
  ${({ isActive, isHighlighted }) => (isActive || isHighlighted) && [tw`after:scale-x-100`]}
`;

export const StyledMenuItemSecondary = styled(StyledMenuItem)`
  ${tw` border-b border-neutrals-n7`}
`;

export const StyledTitle = styled.span`
  ${tw`font-semi-bold`}
  ${tw`font-inter`}
  ${tw`text-primary-p1`}
  ${tw`text-text-cta`}
`;

export const StyledContent = styled.div<{ isActive: boolean }>`
  ${tw`bg-neutrals-n1`}
  ${tw`absolute`}
  ${tw`top-0 right-[-100vw]`}
  ${tw`lg:top-[78px] lg:left-[-28px]`}
  ${tw`flex flex-col lg:flex-row`}
  ${tw`opacity-0`}
  ${tw`w-full h-full lg:h-0 lg:w-fit lg:max-h-[calc(100vh - 150px)]`}
  ${tw`z-[100] lg:z-30`}
  ${tw`overflow-y-scroll lg:overflow-y-auto`}
  ${tw`rounded-xl`}
  ${tw`text-left`}

  transition:
    height none,
    right 0.3s ease-in-out,
    opacity 0.3s ease-in-out;

  ${tw`lg:transition-opacity lg:duration-[0.5s]`}

  ${({ isActive }) =>
    isActive && [
      tw`right-0`,
      tw`lg:border-2 border-primary-p1`,
      tw`opacity-100`,
      tw`lg:h-auto`,
      tw`lg:py-[20px] lg:pl-[12px] lg:pr-[20px]`,
      `
        transition: right 0.3s ease-in-out, opacity 0.3s ease-in-out;
      `,
    ]}
`;

export const StyledColumns = styled.div`
  ${tw`flex flex-col lg:flex-row`}
  ${tw`relative`}
`;

export const StyledColumn = styled.div<{
  isHighlighted: boolean;
  size?: 'small' | 'medium' | 'large' | null;
}>`
  ${({ size }) => {
    switch (size) {
      case 'small':
        return tw`w-full lg:w-[273px]`;
      case 'medium':
        return tw`w-full lg:w-[333px]`;
      case 'large':
        return tw`w-full lg:w-[393px]`;
      default:
        return tw`w-full lg:w-[333px]`;
    }
  }}
  ${tw`relative`}
  ${tw`flex flex-col`}
  ${tw`gap-2 lg:gap-[12px]`}
  ${tw`pt-1 px-2 lg:px-1`}

  ${({ isHighlighted }) => isHighlighted && [tw`bg-ui-colours-ui1`, tw`rounded`]}

  ${StyledLink} {
    ${({ isHighlighted }) => isHighlighted && tw`before:bg-neutrals-n1`}
  }
`;

export const StyledBorder = styled.div`
  ${tw`flex before:h-full before:w-[22px] after:h-full after:w-[22px]`}
  ${tw`before:border-r before:border-neutrals-n7`}
`;
